import {
  DuplicateConflictPayload,
  DuplicateType,
  IAdminRecipientDetail,
  LookupRecipient,
  PatchRecipientPayload,
} from 'store/recipients/types';
import { RecipientFormValues } from './types';

export const NONPROFIT_C3_SUBTYPE = '501C3';

export function hasChariotKeys(miscIds: IAdminRecipientDetail['misc_ids']): boolean {
  if (!miscIds) return false;
  return Object.keys(miscIds).some(key => key.startsWith('chariot_'));
}

export function subtypeChangedToNonC3(
  original: Partial<RecipientFormValues>,
  current: RecipientFormValues
): boolean {
  return current.subtype !== original.subtype && current.subtype !== NONPROFIT_C3_SUBTYPE;
}

export function parseCreate409(
  error: unknown
): { duplicates: LookupRecipient[]; duplicateType: DuplicateType } | null {
  const err = error as { response?: { status?: number; data?: DuplicateConflictPayload } };
  if (err?.response?.status !== 409) return null;
  const payload = err.response?.data;
  return {
    duplicates: payload?.recipients ?? [],
    duplicateType: payload?.duplicate_type ?? 'display_name',
  };
}

export function einChangedToNonNull(
  original: Partial<RecipientFormValues>,
  current: RecipientFormValues
): boolean {
  const origEin = original.recipient_ein ?? '';
  const currEin = current.recipient_ein ?? '';
  return currEin !== '' && currEin !== origEin;
}

export function validateRecipientForm(
  values: RecipientFormValues,
  isEditMode = false
): Record<string, string> {
  const errors: Record<string, string> = {};
  const isCandidate = values.type === 'CANDIDATE';
  if (!isCandidate && values.recipient_ein && !values.recipient_contact_email) {
    errors.recipient_contact_email = 'Email is required when an EIN is provided.';
  }
  // In edit mode, elections are managed separately via CandidateElections (GET/POST races) —
  // elections_selected is only populated by the create-flow's ElectionPicker.
  if (!isEditMode && isCandidate && !values.elections_selected?.length) {
    errors.elections_selected = 'At least one election is required.';
  }
  return errors;
}

// Tab 0 (Recipient Information) errors on any field except elections_selected, which
// belongs to tab 1 (Race Information, candidates only).
export function getTabErrorStatus(
  item: number,
  errors: Record<string, unknown> | undefined
): 'error' | 'success' {
  const tabHasErrors =
    item === 0
      ? Object.keys(errors ?? {}).some(key => key !== 'elections_selected')
      : Boolean(errors?.elections_selected);
  return tabHasErrors ? 'error' : 'success';
}

type FieldMapping = {
  formKey: keyof RecipientFormValues;
  apiKey: keyof PatchRecipientPayload;
};

const FIELD_MAP: FieldMapping[] = [
  { formKey: 'recipient_name', apiKey: 'display_name' },
  { formKey: 'recipient_legal_name', apiKey: 'legal_name' },
  { formKey: 'subtype', apiKey: 'subtype' },
  { formKey: 'recipient_photo_id', apiKey: 'photo_id' },
  { formKey: 'recipient_de_url_id', apiKey: 'de_url_id' },
  { formKey: 'public_search', apiKey: 'is_public_searchable' },
  { formKey: 'recipient_ein', apiKey: 'legal_id' },
  { formKey: 'recipient_contact_name', apiKey: 'primary_contact_name' },
  { formKey: 'recipient_contact_email', apiKey: 'primary_contact_email' },
  { formKey: 'recipient_status', apiKey: 'recipient_status' },
  { formKey: 'stripe_authorized', apiKey: 'is_stripe_authorized' },
  { formKey: 'onboarding_form_complete', apiKey: 'onboarding_form_complete' },
  { formKey: 'public_fundraising_query_str', apiKey: 'public_fundraising_query_str' },
];

function isEmpty(value: unknown): boolean {
  return value === '' || value === null || value === undefined;
}

export function buildPatch(
  original: Partial<RecipientFormValues>,
  current: RecipientFormValues
): PatchRecipientPayload {
  const patch: Record<string, unknown> = {};

  for (const { formKey, apiKey } of FIELD_MAP) {
    const originalVal = original[formKey];
    const currentVal = current[formKey];

    if (originalVal === currentVal) continue;

    if (isEmpty(currentVal) && !isEmpty(originalVal)) {
      patch[apiKey] = null;
    } else if (!isEmpty(currentVal)) {
      patch[apiKey] = currentVal;
    }
  }

  return patch as PatchRecipientPayload;
}
